import {
  appointmentSlot,
  MAX_SCHEMATIC_NODES,
  SCHEMATIC_STROKE_WIDTHS,
  emptyNetworkSchematic,
  isSchematicPlanFull,
  newUuidV7,
  type NetworkSchematic,
  type SchematicNode,
  type SchematicNodeKind,
  type SchematicPoint,
  type SchematicStroke,
  type SchematicStrokeWidth,
} from '@ghp/domain'
import { Button, IconButton, SectionLabel, SegmentedControl } from '@ghp/ui'
import {
  Check,
  Crosshair,
  Grid3x3,
  ImageDown,
  Minus,
  Plus,
  Redo2,
  Trash2,
  TriangleRight,
  Undo2,
} from 'lucide-react'
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import {
  addNode,
  appendPoint,
  createSchematicNode,
  dragNodeEnd,
  edgeToward,
  extendNode,
  insertVertex,
  isNetworkTyped,
  isSeparateNetworkKind,
  moveNode,
  moveVertex,
  removeNode,
  removeVertex,
  setNodeEnds,
  setNodeLevel,
  setNodeNetwork,
  setNodeStyle,
  setNodeText,
  simplifyPath,
  snapToGrid,
  straightenNode,
  structureAt,
  symbolExtent,
} from '@ghp/schematic'
import {
  canRedo,
  canUndo,
  commitSchematic,
  createSchematicHistory,
  redoSchematic,
  undoSchematic,
} from './schematic-history'
import {
  SCHEMATIC_VIEW,
  SchematicShape,
  contentBoxOf,
  fittedTo,
  pannedBy,
  pointAt,
  schematicExportFrame,
  zoomPercent,
  zoomedAt,
  zoomedBy,
  type SchematicViewport,
  type ViewportBox,
} from '@ghp/schematic'
import type { SchematicEditorProps } from './schematic-editor-port'
import {
  createSchematicImagePort,
  schematicFingerprint,
  type SchematicImagePort,
} from '@ghp/schematic'
import {
  SchematicPalette,
  isSeparateOnly,
  isStormwaterOnly,
  resolvedKind,
  typedNode,
  type PaletteNetwork,
} from './SchematicPalette'
import {
  SCHEMATIC_CATALOGUE,
  SCHEMATIC_LEVEL_OPTIONS,
  SCHEMATIC_NETWORK_OPTIONS,
  SCHEMATIC_SEPARATE_NETWORK_OPTIONS,
  SCHEMATIC_STROKE_LABELS,
  SCHEMATIC_STROKE_SWATCHES,
  SCHEMATIC_STROKE_WIDTH_LABELS,
  SCHEMATIC_STRUCTURE_NETWORK_OPTIONS,
  type CatalogueFamily,
  type CatalogueItem,
} from './schematic-labels'
import s from './SchematicEditor.module.css'

/** Le port, plus ce que cette implémentation-ci a besoin de laisser injecter. */
export interface SvgSchematicEditorProps extends SchematicEditorProps {
  /**
   * La rastérisation, injectable — jsdom n'a ni décodeur d'image ni `canvas.toBlob`. Hors du
   * port : c'est un détail de **cette** implémentation, une bibliothèque tierce ayant la sienne.
   */
  image?: SchematicImagePort
}

/**
 * L'éditeur de schéma réseau de l'onglet « Schéma » — **l'implémentation SVG maison**, derrière
 * le port `ports/schematic-editor.ts` qui dit pourquoi elle est remplaçable.
 *
 * ## Les trois choix qui ne se devinent pas
 *
 * 1. **Aucune bibliothèque de canvas** — ni tldraw ni Excalidraw : environ un mégaoctet de SDK
 *    précaché par le Service Worker. L'essai des trois sur la tablette, le 29/09/2026, a confirmé
 *    le choix (#318).
 * 2. **SVG plutôt que `<canvas>`** — le motif est écrit dans `SchematicShape.tsx`. En résumé :
 *    la sélection, le déplacement et l'annulation sont gratuits, là où un canvas impératif
 *    demande d'écrire soi-même le test de collision d'une polyligne.
 * 3. **Le document n'est jamais détenu ici.** Il entre par `value`, il ressort par `onChange` ;
 *    seule la vue (zoom, glissement), l'outil courant et la pile d'annulation vivent dans cet
 *    état. C'est ce qui rend l'éditeur remplaçable sans toucher à ce que la tablette a rangé.
 *
 * ## Le catalogue
 *
 * Les 52 outils de GHP DIGIT (rapport 02 de la rétro-ingénierie), dans la disposition du
 * prototype validé : outils à gauche, plan au centre, style à droite — frames Terrain 1.9b →
 * 1.9g du canvas « Propositions ».
 *
 * ## Le geste, pensé pour un doigt
 *
 * Pointer Events, `touch-action: none`, cibles généreuses, **aucun survol requis** — une
 * tablette n'en a pas. Un geste = un document émis, **au relâchement** : émettre à chaque
 * déplacement du doigt écrirait un document par pixel parcouru dans IndexedDB.
 */
export function SchematicEditor({
  value,
  disabled,
  onChange,
  onExport,
  exportedAt = null,
  exportOutdated = false,
  backdrop = null,
  fill = false,
  image = DEFAULT_IMAGE_PORT,
}: SvgSchematicEditorProps) {
  const boardRef = useRef<SVGSVGElement>(null)

  /**
   * ⚠️ **`touch-action: none` ne suffit pas sur iPad, et c'est la seule raison d'être de cet
   * effet.** WebKit n'applique pas la propriété à un `<svg>` : le doigt qui tire une conduite
   * y est rendu au défilement du panneau — huit pixels de débordement suffisent, mesurés en
   * paysage — et le trait ne part jamais. Chrome l'honore, d'où un défaut que les tablettes
   * Android sur lesquelles l'éditeur a été écrit ne montraient pas. La zone de signature y
   * échappe parce qu'elle dessine dans un `<canvas>`, qui est une boîte HTML.
   *
   * `preventDefault` sur `touchmove` est le refus qui vaut sur tous les navigateurs. Il se
   * pose à la main, et `passive: false` n'est pas un détail : React attache `touchmove` en
   * **passif** sur sa racine, où `preventDefault` n'a aucun effet — un `onTouchMove` de JSX
   * aurait été un correctif qui ne corrige rien, sans que rien ne le dise.
   *
   * ⚠️ Le refus ne se relâche pas sur un contrôle transmis : il vaut ce que vaut la règle CSS,
   * et la lever ici seulement ferait diverger iPad et Android sur la même fiche.
   */
  useEffect(() => {
    const board = boardRef.current
    if (board === null) return

    const refuseScroll = (event: TouchEvent): void => event.preventDefault()
    board.addEventListener('touchmove', refuseScroll, { passive: false })
    return () => board.removeEventListener('touchmove', refuseScroll)
  }, [])

  /** L'outil choisi : un outil de dessin, ou l'identifiant d'un bouton du catalogue. */
  const [tool, setTool] = useState<string>('select')
  const [family, setFamily] = useState<CatalogueFamily['value']>('structures')
  const [network, setNetwork] = useState<PaletteNetwork>('wastewater')
  const [level, setLevel] = useState('0')
  const [grid, setGrid] = useState(false)
  const [stroke, setStroke] = useState<SchematicStroke>('ink')
  const [strokeWidth, setStrokeWidth] = useState<SchematicStrokeWidth>('medium')
  const [view, setView] = useState<SchematicViewport>(SCHEMATIC_VIEW)
  const [selected, setSelected] = useState<string | null>(null)
  /** L'angle choisi sur la forme sélectionnée — celui que « Supprimer l'angle » retire. */
  const [vertex, setVertex] = useState<number | null>(null)
  /**
   * La forme **tracée d'appui en appui** en cours : maison, préau, conduite ou réseau public
   * coudés. Chaque appui à partir du deuxième est rangé, donc « Annuler » retire le dernier angle
   * posé.
   */
  const [draft, setDraft] = useState<string | null>(null)
  /**
   * Le premier appui de ce tracé, **tenu hors du dessin rangé**. ⚠️ Un point seul ne se voit ni
   * ne se touche : rangé, il partait au bureau dès que l'agent changeait d'onglet ou rechargeait,
   * et il élargissait le cadre de l'image d'archive. La forme n'est rangée qu'à son deuxième point.
   */
  const [pending, setPending] = useState<SchematicNode | null>(null)
  /** Où le trait qui suit le doigt aboutit, pendant un tracé d'appui en appui. */
  const [hover, setHover] = useState<SchematicPoint | null>(null)
  const [history, setHistory] = useState(() => createSchematicHistory(value))
  /**
   * Le dessin **pendant** le geste : le trait doit suivre le doigt, et il ne part dans Dexie
   * qu'au relâchement. `null` quand aucun geste n'est en cours — c'est alors `value` qui fait
   * foi, donc ce que la tablette a rangé.
   */
  const [preview, setPreview] = useState<NetworkSchematic | null>(null)
  /**
   * La question « tout effacer ? », posée et pas encore répondue.
   *
   * ⚠️ Un état, pas une boîte de dialogue : le dépôt en a une (`Modal`), et elle serait ici le
   * mauvais outil — elle prend le focus et couvre le plan, c'est-à-dire la seule chose que
   * l'agent doit regarder avant de répondre. C'est le motif de la déconnexion (`app/routes/
   * root.tsx`), au même prix : deux gestes, aucun montage.
   */
  const [clearing, setClearing] = useState(false)
  /** Le rendu en cours, et le dernier échec — l'un ferme le bouton, l'autre porte la phrase. */
  const [rendering, setRendering] = useState(false)
  const [renderFailed, setRenderFailed] = useState(false)
  const gesture = useRef<Gesture | null>(null)
  /** Les doigts posés sur le plan, par pointeur — c'est ce qui reconnaît un pincement. */
  const pointers = useRef(new Map<number, { clientX: number; clientY: number }>())
  /**
   * Le champ « Libellé », tenu par sa réf parce que **sa valeur ne vit que dans le DOM** : il
   * n'est pas piloté, exprès ({@link label}). C'est ce qui oblige à aller la chercher avant que
   * React ne démonte le champ — voir {@link flushLabel}.
   */
  const labelInput = useRef<HTMLInputElement>(null)

  const drawing = withPending(preview ?? value, pending)

  /**
   * ⚠️ L'historique est recalé sur `value` au moment du geste, jamais sur son propre
   * `present` : le brouillon peut avoir changé par un autre chemin (reprise d'une fiche
   * renvoyée, relecture après rechargement), et une pile qui s'ignorerait ferait rendre à
   * « Annuler » un dessin que l'agent n'a jamais eu sous les yeux.
   */
  function commit(next: NetworkSchematic): void {
    setHistory((current) => commitSchematic({ ...current, present: value }, next))
    setPreview(null)
    onChange(next)
  }

  /**
   * ⚠️ La boîte de **contenu**, jamais celle que `getBoundingClientRect()` rend : le plan porte
   * un liseré, et la surface de dessin commence après. C'est le défaut que `SignaturePad`
   * documente avoir corrigé — il ne se voit pas au centre et grandit vers les bords.
   */
  function boxOfBoard(): ViewportBox {
    const board = boardRef.current
    if (board === null) return { left: 0, top: 0, width: 0, height: 0 }

    const bounds = board.getBoundingClientRect()

    return contentBoxOf(bounds, {
      left: borderOf(board, 'left'),
      top: borderOf(board, 'top'),
      right: borderOf(board, 'right'),
      bottom: borderOf(board, 'bottom'),
    })
  }

  /** Le point touché, calé sur la grille quand elle est active. */
  function placed(point: SchematicPoint): SchematicPoint {
    return grid ? snapToGrid(point, GRID_STEP) : point
  }

  /**
   * ⚠️ **Un doigt trace, et un second ne l'interrompt pas.** Une paume posée arrive ici comme un
   * second contact — le plan porte `touch-action: none`, donc le navigateur n'en avale aucun.
   * Sans ce refus, le second appui écrasait le geste en cours et repartait de `value` : la
   * conduite tirée n'existait que dans le brouillon d'écran, elle disparaissait sans qu'aucun
   * « Annuler » ne la ramène, et c'est le brouillon parasite qui partait dans Dexie au
   * relâchement.
   *
   * Le second doigt n'ouvre un **pincement** que lorsque le premier déplace le plan — outil
   * « Déplacer », fond du plan touché en sélection, ou fiche transmise. C'est le seul cas où
   * aucune saisie ne peut se perdre.
   */
  function start(event: ReactPointerEvent<SVGSVGElement>): void {
    pointers.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY })

    const current = gesture.current
    if (current !== null) {
      if (current.kind === 'pan' && pointers.current.size === 2) startPinch(current.pointer, event)
      return
    }

    /*
     * ⚠️ **Verrouillé ne veut pas dire immobile.** Une fiche transmise se relit, et les boutons
     * de zoom répondent toujours : sans cette branche, on grossit un plan qu'on ne peut plus
     * déplacer, et le lecteur reste coincé au centre. Le geste **est** le déplacement — il n'y
     * a pas d'outil à choisir puisque la palette est fermée, et c'est ce que fait n'importe
     * quelle carte. Rien n'est écrit : `pan` ne touche que la vue.
     */
    if (disabled === true) {
      event.currentTarget.setPointerCapture?.(event.pointerId)
      gesture.current = {
        pointer: event.pointerId,
        kind: 'pan',
        client: { x: event.clientX, y: event.clientY },
      }
      return
    }

    // ⚠️ **Avant tout le reste** : ce geste va changer la sélection, donc démonter le champ
    // « Libellé » et emporter ce que l'agent venait d'y taper. Tout ce qui suit travaille sur
    // le document que cette ligne rend, jamais sur `value`.
    const drawn = flushLabel()

    // ⚠️ La main est repartie au plan : la question n'a plus de réponse à attendre. Sans ce
    // désarmement elle resterait posée au pied de la colonne, et le pouce qui y retombe plus
    // tard appuie sur « Effacer » — le geste que la confirmation existe pour éviter.
    setClearing(false)

    // Capturer le pointeur : un doigt qui sort de la zone en tirant une conduite ne doit pas
    // couper le trait au milieu. ⚠️ Optionnel — jsdom ne l'implémente pas, et une capacité
    // absente ne doit pas empêcher de dessiner.
    event.currentTarget.setPointerCapture?.(event.pointerId)

    const raw = pointAt(view, boxOfBoard(), event)
    const point = placed(raw)
    const pointer = event.pointerId

    if (tool === 'pan') {
      gesture.current = { pointer, kind: 'pan', client: { x: event.clientX, y: event.clientY } }
      return
    }

    if (tool === 'select') {
      const handle = handleAt(event.target)
      if (handle !== null && selected !== null) {
        startHandle(handle, drawn, selected, point, pointer)
        return
      }

      const id = nodeIdAt(event.target)
      setSelected(id)
      setVertex(null)
      // Le fond du plan, touché en sélection, déplace le plan : c'est ce qui laisse pincer sans
      // changer d'outil, et la main n'y écrit rien.
      gesture.current =
        id === null
          ? { pointer, kind: 'pan', client: { x: event.clientX, y: event.clientY } }
          : { pointer, kind: 'move', id, point: raw }
      return
    }

    // La gomme efface ce qu'elle touche, puis tout ce que le doigt traverse tant qu'il reste
    // posé : un seul geste, donc un seul « Annuler » pour tout ramener.
    if (tool === 'eraser') {
      gesture.current = { pointer, kind: 'erase' }
      erase(drawn, nodeIdAt(event.target))
      return
    }

    if (draft !== null && continueDraft(withPending(drawn, pending), draft, raw, point)) return

    // ⚠️ **C'est ici que le compte de formes se borne, et nulle part ailleurs.** Le modèle, lui,
    // relit sans refuser : un `.max` sur `nodes` faisait tomber le relevé entier au dépôt, donc
    // un contrôle verrouillé et intransmissible. Sélection et glissement restent ouverts — sans
    // eux, un plan saturé serait un cul-de-sac dont l'agent ne pourrait rien retirer.
    if (planFull) return

    const node = nodeFor(tool, drawn, raw, point)
    if (node === null) return

    if (TAP_DRAWN.has(node.kind)) {
      setPending(node)
      setSelected(null)
      setDraft(node.id)
      return
    }

    gesture.current = { pointer, kind: 'draw', id: node.id, shape: node.kind }
    setSelected(node.id)
    setPreview(addNode(drawn, node))
  }

  function erase(drawn: NetworkSchematic, id: string | null): void {
    if (id === null) return
    setPreview((sketch) => removeNode(sketch ?? drawn, id))
    setSelected((current) => (current === id ? null : current))
  }

  /** La forme que l'outil courant pose sous le doigt, ou `null` si l'outil ne pose rien. */
  function nodeFor(
    current: string,
    drawn: NetworkSchematic,
    raw: SchematicPoint,
    point: SchematicPoint,
  ): SchematicNode | null {
    const item = catalogueItem(current)
    const id = newUuidV7()

    // ⚠️ La maison et le préau n'ont pas de colonne « Style » : sans ce noir posé à la main, ils
    // prenaient en silence la couleur du dernier trait libre (recette du 20/10, ligne 9).
    if (item === null) {
      const free = FREE_SHAPES.has(current)
      return createSchematicNode({
        id,
        kind: current as SchematicNodeKind,
        points: startingPoints(current, point),
        stroke: free ? stroke : 'ink',
        strokeWidth: free ? strokeWidth : 'medium',
      })
    }

    const kind = resolvedKind(item, network)
    if (isSeparateOnly(item) && network !== 'wastewater' && network !== 'stormwater') return null
    if (isStormwaterOnly(item) && network !== 'stormwater') return null

    if (item.text !== undefined) {
      return createSchematicNode({
        id,
        kind,
        points: [point],
        stroke: 'ink',
        strokeWidth: 'medium',
        text: item.text,
        ...(item.kind === 'roomLabel' ? { level: Number(level) } : {}),
      })
    }

    // Une conduite ou un réseau public qui part d'un ouvrage s'y accroche : son premier point
    // est le centre de l'ouvrage, recalé sur son bord dès que la direction est connue.
    const from = LINEAR.has(kind) ? structureAt(drawn, raw) : null
    const origin = from?.points[0] ?? point
    const created = typedNode(
      createSchematicNode({
        id,
        kind,
        points: startingPoints(kind, origin),
        stroke: 'ink',
        strokeWidth: kind === 'publicNetwork' ? 'thick' : strokeFor(kind),
        ...(item.typed === true && network !== 'ink' ? { network } : {}),
      }),
      item,
      network,
    )
    return from === null ? created : { ...created, from: from.id }
  }

  /**
   * Un appui pendant un tracé d'appui en appui : ferme la maison, accroche la conduite, ou pose
   * l'angle suivant. Rend `false` quand le tracé n'existe plus — l'appui repart alors de zéro.
   */
  function continueDraft(
    drawn: NetworkSchematic,
    id: string,
    raw: SchematicPoint,
    point: SchematicPoint,
  ): boolean {
    const node = drawn.nodes.find((candidate) => candidate.id === id)
    if (node === undefined) {
      setDraft(null)
      setPending(null)
      return false
    }

    const first = node.points[0]
    if (
      node.kind === 'polygon' &&
      node.points.length >= 3 &&
      first !== undefined &&
      Math.hypot(point.x - first.x, point.y - first.y) <= CLOSE_RADIUS * unitOf(view)
    ) {
      finishDraft(drawn)
      return true
    }

    // Ce qui suit range la forme : le premier point y entre avec elle.
    setPending(null)
    let next = drawn
    // Le départ accroché se recale sur le bord de l'ouvrage, vers le premier coude.
    if (LINEAR.has(node.kind) && node.points.length === 1 && typeof node.from === 'string') {
      const origin = drawn.nodes.find((candidate) => candidate.id === node.from)
      if (origin !== undefined) next = moveVertex(next, id, 0, edgeToward(origin, point))
    }

    const target = LINEAR.has(node.kind) ? structureAt(drawn, raw) : null
    if (target !== null && target.id !== node.from) {
      const last = next.nodes.find((candidate) => candidate.id === id)?.points.at(-1) ?? point
      commit(setNodeEnds(appendPoint(next, id, edgeToward(target, last)), id, { to: target.id }))
      setDraft(null)
      setHover(null)
      return true
    }

    commit(appendPoint(next, id, point))
    return true
  }

  /**
   * Termine le tracé d'appui en appui. Un premier point resté seul n'a jamais été rangé : il
   * s'oublie, c'est un geste avorté et pas une saisie ({@link pending}).
   */
  function finishDraft(drawn: NetworkSchematic = value): void {
    // La forme finie est prise : son champ « Libellé » s'ouvre, pour la nommer « Maison ».
    if (drawn.nodes.some((node) => node.id === draft)) setSelected(draft)
    setPending(null)
    setDraft(null)
    setHover(null)
  }

  function startHandle(
    handle: Handle,
    drawn: NetworkSchematic,
    id: string,
    point: SchematicPoint,
    pointer: number,
  ): void {
    const node = drawn.nodes.find((candidate) => candidate.id === id)
    if (node === undefined) return

    if (handle.kind === 'insert') {
      const next = insertVertex(drawn, id, handle.index, point)
      commit(next)
      setVertex(handle.index)
      gesture.current = { pointer, kind: 'vertex', id, index: handle.index, base: next }
      return
    }

    setVertex(handle.index)
    gesture.current = { pointer, kind: 'vertex', id, index: handle.index, base: drawn }
  }

  /** Deux doigts posés : le plan se zoome autour de leur milieu, sans rien écrire. */
  function startPinch(first: number, event: ReactPointerEvent<SVGSVGElement>): void {
    const other = pointers.current.get(first)
    if (other === undefined) return

    gesture.current = {
      pointer: first,
      kind: 'pinch',
      second: event.pointerId,
      distance: Math.max(1, distanceOf(other, event)),
      view,
      client: {
        clientX: (other.clientX + event.clientX) / 2,
        clientY: (other.clientY + event.clientY) / 2,
      },
    }
  }

  function extend(event: ReactPointerEvent<SVGSVGElement>): void {
    if (pointers.current.has(event.pointerId)) {
      pointers.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY })
    }

    const current = gesture.current
    const box = boxOfBoard()

    if (current?.kind === 'pinch') {
      if (event.pointerId !== current.pointer && event.pointerId !== current.second) return
      const a = pointers.current.get(current.pointer)
      const b = pointers.current.get(current.second)
      if (a === undefined || b === undefined) return
      setView(zoomedAt(current.view, box, current.client, distanceOf(a, b) / current.distance))
      return
    }

    if (current === null) {
      // Le trait qui suit le doigt — ou la souris — entre deux appuis d'un tracé.
      if (draft !== null) setHover(placed(pointAt(view, box, event)))
      return
    }

    if (current.pointer !== event.pointerId) return

    if (current.kind === 'pan') {
      const delta = { x: event.clientX - current.client.x, y: event.clientY - current.client.y }
      gesture.current = { ...current, client: { x: event.clientX, y: event.clientY } }
      setView((currentView) => pannedBy(currentView, box, delta))
      return
    }

    if (current.kind === 'erase') {
      // ⚠️ Le doigt est capturé par le plan : `event.target` reste le plan tout le geste. C'est
      // le point de l'écran qui dit la forme traversée.
      // Hors du plan — la palette dessine aussi des formes —, rien ne s'efface.
      const crossed = document.elementFromPoint?.(event.clientX, event.clientY)
      const onBoard = crossed != null && boardRef.current?.contains(crossed) === true
      erase(value, onBoard ? nodeIdAt(crossed) : null)
      return
    }

    const raw = pointAt(view, box, event)
    const point = placed(raw)

    if (current.kind === 'move') {
      const delta = { x: raw.x - current.point.x, y: raw.y - current.point.y }
      gesture.current = { ...current, point: raw }

      // ⚠️ **Un écart nul n'ouvre pas de brouillon**, et c'est ce qui sépare « sélectionner »
      // de « déplacer ». Toucher une forme pour la sélectionner émet un déplacement du doigt,
      // que la tablette rapporte au dixième d'unité près : sans cela, le geste le plus courant
      // de l'outil réécrivait le relevé entier dans Dexie et consommait une des quarante places
      // de la pile d'annulation. Un déplacement réel, fût-il d'une unité, reste enregistré —
      // c'est une retouche, et l'agent peut vouloir la reprendre.
      if (delta.x === 0 && delta.y === 0) return

      setPreview((sketch) => moveNode(sketch ?? value, current.id, delta))
      return
    }

    if (current.kind === 'vertex') {
      setPreview(moveVertex(current.base, current.id, current.index, point))
      return
    }

    if (isAnchored(current.shape)) return

    setPreview((sketch) => {
      const base = sketch ?? value

      return current.shape === 'freehand'
        ? extendNode(base, current.id, raw)
        : dragNodeEnd(base, current.id, point)
    })
  }

  function end(event: ReactPointerEvent<SVGSVGElement>): void {
    pointers.current.delete(event.pointerId)

    const current = gesture.current
    if (current === null) return

    if (current.kind === 'pinch') {
      if (event.pointerId === current.pointer || event.pointerId === current.second) {
        gesture.current = null
      }
      return
    }

    if (current.pointer !== event.pointerId) return

    gesture.current = null

    if (current.kind === 'pan') return
    // Rien n'a bougé — une touche sur une forme déjà posée, par exemple : il n'y a pas de geste
    // à enregistrer, et en enregistrer un remplirait la pile d'annulation d'états identiques.
    if (preview === null) return

    if (current.kind === 'draw' && isVoidDrag(preview, current.id)) {
      // Une conduite ou un réseau public touchés sans être tirés : le tracé se poursuit d'appui
      // en appui, pour les poser coudés comme dans GHP DIGIT.
      if (LINEAR.has(current.shape)) {
        const node = preview.nodes.find((candidate) => candidate.id === current.id)
        if (node !== undefined) setPending({ ...node, points: node.points.slice(0, 1) })
        setPreview(null)
        setSelected(null)
        setDraft(current.id)
        return
      }

      // ⚠️ **Une forme tirée qui n'a pas été tirée n'est pas une saisie à préserver.** Un
      // rectangle, une ellipse, une ligne naissent avec deux points confondus et s'étirent au
      // glissement : relâchés sur place, ils écrivaient une forme de taille nulle, que
      // `width="0"` rend invisible **et** intouchable — donc impossible à re-sélectionner pour
      // la supprimer. Elle élargissait pourtant le cadre de l'image d'archive, où une conduite
      // tombait de 62 % à 16 % de la largeur. Rien n'est perdu ici : c'est un geste avorté, pas
      // un trait (M21).
      setPreview(null)
      setSelected(null)
      return
    }

    if (current.kind === 'draw' && LINEAR.has(current.shape)) {
      commit(attached(preview, current.id))
      return
    }

    if (current.kind === 'vertex') {
      commit(reattached(preview, current.id, current.index))
      return
    }

    if (current.kind === 'draw' && current.shape === 'freehand') {
      const tolerance = FREEHAND_TOLERANCE * unitOf(view)
      commit(mapPoints(preview, current.id, (points) => simplifyPath(points, tolerance)))
      return
    }

    commit(preview)
  }

  /**
   * Une conduite tirée d'un geste : chaque extrémité posée sur un ouvrage s'y accroche et
   * s'arrête à son bord, pour que la pointe se voie.
   */
  function attached(sketch: NetworkSchematic, id: string): NetworkSchematic {
    const node = sketch.nodes.find((candidate) => candidate.id === id)
    const first = node?.points[0]
    const last = node?.points.at(-1)
    if (node === undefined || first === undefined || last === undefined) return sketch

    const origin =
      typeof node.from === 'string'
        ? sketch.nodes.find((candidate) => candidate.id === node.from)
        : undefined
    const target = structureAt(sketch, last)
    const arrival = target !== null && target.id !== node.from ? target : undefined

    const start = origin === undefined ? first : edgeToward(origin, arrival?.points[0] ?? last)
    const finish = arrival === undefined ? last : edgeToward(arrival, start)
    const next = mapPoints(sketch, id, (points) => [start, ...points.slice(1, -1), finish])

    return arrival === undefined ? next : setNodeEnds(next, id, { to: arrival.id })
  }

  function undo(): void {
    // Le premier point seul n'a rien rangé : l'annuler abandonne le tracé, sans toucher au dessin.
    if (pending !== null) {
      finishDraft()
      return
    }
    // ⚠️ Pendant un tracé d'appui en appui, « Annuler » retire le dernier angle posé et le tracé
    // continue : chaque appui à partir du deuxième a été rangé comme un geste. Le retrait du
    // deuxième ôte la forme du dessin ; son premier point revient en attente.
    const previous = undoSchematic({ ...history, present: value })
    const node = value.nodes.find((candidate) => candidate.id === draft)
    if (node !== undefined && !previous.present.nodes.some((candidate) => candidate.id === draft)) {
      setPending({ ...node, points: node.points.slice(0, 1) })
    }
    setHistory(previous)
    onChange(previous.present)
  }

  function redo(): void {
    const next = redoSchematic({ ...history, present: value })
    setHistory(next)
    onChange(next.present)
  }

  /** Retire la forme sélectionnée — ou seulement son angle choisi, s'il y en a un. */
  function remove(): void {
    if (selected === null) return
    if (vertex !== null && (selectedNode?.points.length ?? 0) > 1) {
      commit(removeVertex(value, selected, vertex))
      setVertex(null)
      return
    }
    commit(removeNode(value, selected))
    setSelected(null)
  }

  /** « Aligner » : les murs presque droits de la forme sélectionnée se redressent. */
  function align(): void {
    if (selected === null) return
    commit(straightenNode(value, selected))
  }

  /** Changer d'outil termine le tracé en cours : on ne continue pas une maison avec un regard. */
  function chooseTool(next: string): void {
    if (draft !== null) finishDraft()
    setTool(next)
    if (next !== 'select') setVertex(null)
  }

  /**
   * Choisir une famille garde un réseau qu'elle connaît : « Neutre » n'existe que pour un
   * ouvrage, et un réseau public ou une conduite en portent toujours un.
   */
  function chooseFamily(next: CatalogueFamily['value']): void {
    setFamily(next)
    if (next === 'networks' && network === 'ink') setNetwork('wastewater')
  }

  /**
   * Range le libellé **tapé mais pas encore relâché**, et rend le document à jour.
   *
   * ⚠️ **Sans cet appel, le libellé d'une annotation est perdu à tous les coups.** Le champ n'est
   * pas piloté et ne s'enregistre qu'au `blur` ; or le geste suivant de l'agent — retourner sur
   * le plan poser la forme d'après — change la sélection, et React **démonte** alors le champ
   * dans le même événement, avant que le navigateur n'ait déplacé le focus. Un composant démonté
   * n'émet pas de `blur` : la frappe partait avec lui, sans erreur, et l'agent voyait son point
   * d'ancrage rester nu. Seul un départ qui **ne change pas la sélection** — la tabulation, un
   * appui sur un bouton de la colonne — laissait le `blur` arriver, et c'est exactement ce que
   * les tests faisaient.
   *
   * ⚠️ **Rend le document plutôt que de compter sur `value`** : l'appelant poursuit dans le même
   * tour, où la prop n'a pas encore été remplacée par le parent. Bâtir la forme suivante sur
   * `value` réécrirait le libellé qu'on vient de ranger.
   */
  function flushLabel(): NetworkSchematic {
    const typed = labelInput.current?.value

    // ⚠️ **L'aperçu de frappe tombe dans tous les cas**, y compris quand rien n'a changé : le
    // geste qui suit ouvre le sien, et un aperçu resté là partirait dans Dexie à son
    // relâchement — un état d'historique de plus pour un document identique.
    setPreview(null)

    if (typed === undefined || committedNode === null || typed === (committedNode.text ?? '')) {
      return value
    }

    const next = setNodeText(value, committedNode.id, typed)
    commit(next)

    return next
  }

  /**
   * ⚠️ **Au relâchement du champ, pas à la frappe** — la même promesse que celle du port pour un
   * tracé. Émettre par lettre écrivait un document, un état d'historique et un enregistrement
   * Dexie par caractère : un libellé de quarante signes vidait la pile d'annulation du dessin,
   * et chaque frappe réécrivait le relevé entier, dessin compris.
   */
  function label(text: string): void {
    if (selected === null || text === (committedNode?.text ?? '')) return

    commit(setNodeText(value, selected, text))
  }

  /**
   * La frappe **sur le plan**, lettre à lettre — et dans l'aperçu, pas dans le document.
   *
   * ⚠️ C'est le même aperçu que le trait qui suit le doigt, et pour la même raison : sans lui,
   * l'agent tape dans une colonne muette, à l'autre bout de l'écran de la forme qu'il nomme, et
   * ne découvre ce qu'il a écrit qu'en quittant le champ. Avec lui, le retour est immédiat sans
   * qu'une seule lettre n'écrive dans Dexie ni ne consomme une des quarante places de la pile
   * d'annulation — ce que la promesse « au relâchement, pas à la frappe » interdisait
   * jusqu'ici, faute de cette distinction entre voir et ranger.
   */
  function previewLabel(text: string): void {
    if (selected === null) return

    setPreview((sketch) => setNodeText(sketch ?? value, selected, text))
  }

  /**
   * Efface **tout** le plan, la question une fois répondue.
   *
   * ⚠️ Passe par `commit`, donc l'effacement entre dans la pile : « Annuler » le reprend. La
   * confirmation ne garantit rien toute seule — sur une tablette tenue à bout de bras, le doigt
   * part quand même — et un relevé de terrain ne se redessine pas de mémoire. Les deux ensemble,
   * pas l'une ou l'autre.
   */
  function clearAll(): void {
    setPending(null)
    setDraft(null)
    commit(emptyNetworkSchematic())
    setSelected(null)
    setClearing(false)
  }

  /**
   * « Recentrer » — le seul contrôle qui **rapproche** du dessin.
   *
   * ⚠️ Les deux autres n'y arrivent pas : le glissement n'a aucune borne, et les boutons de zoom
   * opèrent autour du centre de la vue courante. Cinq glissements de la main laissaient donc
   * l'agent, ganté et debout, devant un plan blanc à cinq mille unités de son premier trait —
   * et s'il redessinait là, le document partait au bureau avec deux copies du branchement.
   *
   * Le cadre du dessin est celui de l'export : le même calcul, donc la même boîte englobante.
   */
  function recenter(): void {
    const frame = schematicExportFrame(value)

    if (frame === null) return

    setView(fittedTo(frame.view))
  }

  /**
   * « Télécharger l'image » — l'« Export JPEG » de la maquette, renommé à la recette du 13-14/10
   * (#439) : il range l'image d'archive du contrôle (M11), que le conteneur télécharge aussi.
   * L'archive se refaisant déjà toute seule (ci-dessous), le téléchargement est le seul effet
   * propre au bouton — d'où son nom.
   *
   * ⚠️ **Le cadre suit le dessin, jamais la vue de l'agent** (`schematicExportFrame`) : exporter
   * ce qu'il a sous les yeux livrerait au bureau une image dont la moitié du branchement est
   * hors champ, selon l'endroit où il avait zoomé.
   *
   * ⚠️ **Un échec ne coûte rien** : le schéma vit en JSON dans le relevé, l'image n'en est que
   * la copie d'archive. On n'attache rien et on ne touche pas au dessin — mais **on le dit**
   * (M21) : un bouton qui ne produit rien sans un mot se lit comme une panne de l'app, et
   * l'agent n'a aucun autre moyen de savoir que l'archive du bureau restera sans image.
   *
   * ⚠️ **Le bouton se ferme pendant le rendu**, et ce n'est pas qu'une politesse : deux appuis
   * rapides lisaient le même instantané des pièces, tiraient deux identifiants et écrivaient
   * deux images d'archive du même contrôle — l'invalidation des pièces étant asynchrone.
   */
  function exportImage(origin: 'button' | 'auto'): void {
    const board = boardRef.current
    const frame = schematicExportFrame(value)

    if (board === null || frame === null || rendering) return

    // ⚠️ Le dessin est **saisi à l'appui**, pas relu au retour : le rendu est asynchrone, et un
    // trait posé entre-temps ferait créditer l'image d'un dessin qu'elle ne montre pas — donc
    // taire exactement l'écart qu'on cherche à dire.
    const rendered = value

    setRendering(true)
    setRenderFailed(false)

    void image.render(board, frame).then((jpeg) => {
      setRendering(false)
      setRenderFailed(jpeg === null)

      if (jpeg !== null) onExport?.(jpeg, rendered, origin)
    })
  }

  /**
   * ⚠️ **L'image d'archive se refait toute seule** dès que l'agent cesse de dessiner, si elle
   * manque ou ne montre plus le dessin. Le rapport PDF n'imprime qu'elle : laissée au seul bouton
   * « JPEG » (aujourd'hui « Télécharger l'image »), elle manquait, et le rapport annonçait
   * « aucun schéma transmis » sous un plan que le bureau voyait bien (constaté en production le
   * 08/10/2026).
   *
   * Sans tableau de dépendances, exprès : chaque rendu — donc chaque mouvement du doigt — repousse
   * l'échéance. L'empreinte retenue empêche de relancer un rendu raté sur le même dessin.
   */
  const autoRendered = useRef<string | null>(null)

  useEffect(() => {
    if (onExport === undefined || disabled === true || value.nodes.length === 0) return
    if (exportedAt !== null && !exportOutdated) return

    const timer = setTimeout(() => {
      const print = schematicFingerprint(value)
      if (autoRendered.current === print) return

      autoRendered.current = print
      exportImage('auto')
    }, AUTO_EXPORT_DELAY_MS)

    return () => clearTimeout(timer)
  })

  const selectedNode = drawing.nodes.find((node) => node.id === selected) ?? null
  /**
   * La forme sélectionnée **telle qu'elle est rangée**, aperçu exclu.
   *
   * ⚠️ C'est elle, et jamais `selectedNode`, qui dit si le libellé a changé : depuis que la
   * frappe se voit sur le plan, l'aperçu porte déjà ce que l'agent vient de taper. Comparer à
   * l'aperçu revient à se demander si ce qu'on lit est ce qu'on lit — la réponse est toujours
   * oui, et le libellé ne serait jamais rangé.
   */
  const committedNode = value.nodes.find((node) => node.id === selected) ?? null
  const draftNode =
    draft === null ? null : (drawing.nodes.find((node) => node.id === draft) ?? null)
  /**
   * ⚠️ `nodes.length` et non `schematicExportFrame(value)` : le cadrage parcourt **tous** les
   * points de **toutes** les formes pour n'en tirer ici qu'un booléen, et il était recalculé à
   * chaque rendu — donc à chaque pixel parcouru par le crayon, sur le dessin qui grossit.
   */
  const empty = value.nodes.length === 0
  const exportable = disabled !== true && !empty
  const planFull = isSchematicPlanFull(value)
  const alignable = selectedNode !== null && ALIGNABLE.has(selectedNode.kind)
  // ⚠️ Un outil libre montre son style **quelle que soit la sélection** : le regard qu'on vient de
  // poser reste pris, et il cachait les couleurs du crayon choisi juste après (recette du 20/10).
  const styled =
    tool !== 'select'
      ? FREE_SHAPES.has(tool)
      : selectedNode === null || FREE_SHAPES.has(selectedNode.kind)
  // En Sélection, les pastilles repeignent la forme prise, et montrent la sienne : elles ne
  // réglaient que la forme suivante, et la forme prise restait noire sous un « Eaux usées » coché.
  const restyled =
    tool === 'select' && selectedNode !== null && FREE_SHAPES.has(selectedNode.kind)
      ? selectedNode
      : null
  const shownStroke = restyled?.stroke ?? stroke
  const shownStrokeWidth = restyled?.strokeWidth ?? strokeWidth
  function restyle(style: { stroke?: SchematicStroke; strokeWidth?: SchematicStrokeWidth }): void {
    if (restyled !== null) commit(setNodeStyle(value, restyled.id, style))
  }
  const hint = hintFor(tool, draftNode)

  return (
    <div
      className={s.root}
      data-disabled={disabled === true ? '' : undefined}
      data-fill={fill ? '' : undefined}
    >
      <SchematicPalette
        tool={tool}
        family={family}
        network={network}
        level={level}
        disabled={disabled}
        onTool={chooseTool}
        onFamily={chooseFamily}
        onNetwork={setNetwork}
        onLevel={setLevel}
      />

      <div className={s.board}>
        <div className={s.actions}>
          <Button
            tone="ghost"
            size="sm"
            disabled={disabled === true || (!canUndo(history) && pending === null)}
            onClick={undo}
          >
            <Undo2 size={18} aria-hidden /> Annuler
          </Button>
          <Button
            tone="ghost"
            size="sm"
            disabled={disabled === true || !canRedo(history)}
            onClick={redo}
          >
            <Redo2 size={18} aria-hidden /> Rétablir
          </Button>
          <Button
            tone="ghost"
            size="sm"
            disabled={disabled === true || selectedNode === null}
            onClick={remove}
          >
            <Trash2 size={18} aria-hidden /> {vertex === null ? 'Supprimer' : 'Supprimer l’angle'}
          </Button>
          <Button tone="ghost" size="sm" disabled={disabled === true || !alignable} onClick={align}>
            <TriangleRight size={18} aria-hidden /> Aligner
          </Button>
          {draftNode !== null && (
            <Button size="sm" onClick={() => finishDraft()}>
              <Check size={18} aria-hidden /> Terminer
            </Button>
          )}

          {/*
           * **Tout effacer — à l'autre bout de la barre, et c'est tout l'écart qu'on lui donne.**
           *
           * ⚠️ Ce bouton est le seul de l'éditeur qu'on veuille rendre **malcommode** : il jette
           * un relevé de terrain entier, qui ne se redessine pas de mémoire. Il ne partage donc
           * ni le bord ni le voisinage de « Supprimer », qui ne retire qu'une forme — la marge
           * automatique le pousse au bord opposé, et l'icône seule le distingue de trois boutons
           * qui portent tous un mot. Ne pas le « ramener près des autres pour l'alignement » :
           * cet écart **est** sa fonction, et la confirmation en dessous est la seconde moitié.
           *
           * ⚠️ Le rouge tient au trait et au liseré, jamais à un aplat : un pavé rouge dans une
           * barre d'outils appelle le doigt au lieu de l'éloigner. L'aplat est réservé au bouton
           * de confirmation, c'est-à-dire au seul moment où l'agent a déjà dit qu'il voulait
           * effacer.
           */}
          <div className={s.clearRow} data-asking={clearing ? '' : undefined}>
            {clearing ? (
              <>
                {/* La question **avant** les réponses, sur la même ligne : c'est elle qui donne
                    son sens à « Effacer », qui sans elle se lit comme le « Supprimer » de la
                    barre — lequel ne retire qu'une forme. */}
                <span className={s.clearAsk}>Tout effacer ?</span>
                <Button tone="danger" size="sm" onClick={clearAll}>
                  Effacer
                </Button>
                {/* « Garder », pas « Annuler » : la barre porte déjà un « Annuler », à trois
                    boutons de là, qui défait le dernier geste. Deux boutons du même nom sur la
                    même ligne, dont l'un renonce et l'autre revient en arrière, c'est une erreur
                    qu'on ne commet qu'une fois — et sur le mauvais dessin. */}
                {/* Ton neutre et non fantôme : les deux réponses partagent la ligne à parts
                    égales, et une réponse qui n'a l'air que d'un mot n'a pas le poids visuel de
                    celle qui détruit. La cible est la même — l'aspect doit l'être aussi. */}
                <Button tone="neutral" size="sm" onClick={() => setClearing(false)}>
                  Garder
                </Button>
              </>
            ) : (
              <IconButton
                label="Effacer tout le schéma"
                className={s.clear}
                disabled={disabled === true || empty}
                onClick={() => setClearing(true)}
              >
                <Trash2 size={18} aria-hidden />
              </IconButton>
            )}
          </div>
        </div>

        {/*
         * ⚠️ `role="img"` sur une surface éditable est un compromis assumé, le même que celui de
         * la zone de signature : le dessin n'a pas de contenu accessible et ne s'opère pas au
         * clavier. Ce n'est pas une équivalence — c'est une limite connue, à rouvrir le jour où
         * un parcours clavier sera demandé, et à ne pas masquer derrière un `role` plus flatteur.
         *
         * ⚠️ `onLostPointerCapture` est le **filet du geste unique** : sans lui, un contact dont
         * le relâchement n'arrive jamais — rotation d'écran, fenêtre qui perd le focus — laisse
         * le geste ouvert, et l'éditeur refuse tous les suivants sans qu'aucun message ne le
         * dise. Sur un relâchement normal, cet événement suit le précédent et ne trouve plus
         * rien à refermer.
         */}
        <svg
          ref={boardRef}
          className={s.canvas}
          role="img"
          aria-label="Plan du schéma réseau"
          viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
          onPointerDown={start}
          onPointerMove={extend}
          onPointerUp={end}
          onPointerCancel={end}
          onLostPointerCapture={end}
        >
          {grid && <GridLines view={view} />}
          {backdrop !== null && (
            <g data-backdrop data-export="skip" pointerEvents="none" opacity={0.35}>
              {backdrop.nodes.map((node) => (
                <SchematicShape key={node.id} node={node} selected={false} />
              ))}
            </g>
          )}
          {drawing.nodes.map((node) => (
            <SchematicShape
              key={node.id}
              // ⚠️ Une maison en cours de tracé reste **ouverte** : fermée d'office, elle tirait
              // un mur fantôme du dernier angle au premier, que l'agent n'a pas posé.
              node={node.id === draft && node.kind === 'polygon' ? { ...node, kind: 'line' } : node}
              selected={node.id === selected}
            />
          ))}
          {/* Les repères d'édition : ils aident la main, et l'image d'archive les laisse de côté
              (`paintedCopy` retire tout ce qui porte `data-export="skip"`). */}
          <g data-export="skip">
            {tool === 'select' && selectedNode !== null && disabled !== true && (
              <Handles node={selectedNode} unit={unitOf(view)} vertex={vertex} />
            )}
            {draftNode !== null && (
              <DraftMarks
                node={draftNode}
                hover={hover}
                unit={unitOf(view)}
                origin={
                  typeof draftNode.from === 'string'
                    ? (drawing.nodes.find((node) => node.id === draftNode.from) ?? null)
                    : null
                }
              />
            )}
          </g>
        </svg>

        <div className={s.zoom}>
          <button
            type="button"
            className={s.tool}
            aria-label="Grille aimantée"
            aria-pressed={grid}
            disabled={disabled}
            onClick={() => setGrid((current) => !current)}
          >
            <Grid3x3 size={18} aria-hidden />
          </button>
          <button
            type="button"
            className={s.tool}
            aria-label="Recentrer"
            disabled={empty}
            onClick={recenter}
          >
            <Crosshair size={18} aria-hidden />
          </button>
          <button
            type="button"
            className={s.tool}
            aria-label="Dézoomer"
            onClick={() => setView(zoomedBy(view, 1 / 1.25))}
          >
            <Minus size={18} aria-hidden />
          </button>
          <span className={s.zoomValue}>{zoomPercent(view)} %</span>
          <button
            type="button"
            className={s.tool}
            aria-label="Zoomer"
            onClick={() => setView(zoomedBy(view, 1.25))}
          >
            <Plus size={18} aria-hidden />
          </button>
        </div>
      </div>

      <div className={s.style} role="group" aria-label="Style">
        {hint !== null && <p className={s.hint}>{hint}</p>}

        {selectedNode !== null && (
          <>
            <SectionLabel>Sélection</SectionLabel>
            {isNetworkTyped(selectedNode.kind) && (
              <>
                <p className={s.styleLabel}>Réseau</p>
                <SegmentedControl<PaletteNetwork>
                  className={s.switch}
                  label="Réseau de l’élément"
                  options={networkOptionsOf(selectedNode.kind)}
                  value={networkOf(selectedNode)}
                  disabled={disabled}
                  onValueChange={(next) => commit(setNodeNetwork(value, selectedNode.id, next))}
                />
              </>
            )}
            {selectedNode.kind === 'roomLabel' && (
              <>
                <p className={s.styleLabel}>Étage</p>
                <SegmentedControl
                  className={s.switch}
                  label="Étage de l’étiquette"
                  options={SCHEMATIC_LEVEL_OPTIONS}
                  value={String(typeof selectedNode.level === 'number' ? selectedNode.level : 0)}
                  disabled={disabled}
                  onValueChange={(next) =>
                    commit(setNodeLevel(value, selectedNode.id, Number(next)))
                  }
                />
              </>
            )}
          </>
        )}

        {/* L'étiquette de la forme sélectionnée — les « EU », « EP » et « épandage » de la
            maquette.

            ⚠️ **Monté à la sélection, pas grisé en permanence.** Le champ occupait la colonne
            dès le premier écran, vide et inerte : l'agent cessait de le voir, et l'outil
            « Texte » n'avait plus alors que son point d'ancrage à montrer — une annotation
            posée, jamais nommée, qui part vide dans l'image d'archive que le bureau relit.
            Le champ est à l'autre bout de l'écran, et c'est voulu sur une tablette : la
            colonne ne doit pas tomber sous la paume qui dessine. C'est donc son **apparition**
            qui porte le signal — le seul mouvement de la colonne au moment où le doigt se
            relève — et l'accent de bordure la rattache à la forme qui vient d'être prise, qui
            s'éclaire du même jeton sur le plan. */}
        {selectedNode !== null && (
          <label className={s.labelField}>
            Libellé
            {/* ⚠️ Champ **non piloté**, remonté par sa clé à chaque changement de sélection :
                c'est ce qui laisse la frappe vivre dans le DOM jusqu'au départ du champ. Sans la
                clé, sélectionner une autre forme garderait le texte de la précédente à
                l'écran. */}
            <input
              key={selected ?? 'none'}
              ref={labelInput}
              type="text"
              className={s.input}
              defaultValue={selectedNode.text ?? ''}
              disabled={disabled === true}
              onChange={(event) => previewLabel(event.target.value)}
              onBlur={(event) => label(event.target.value)}
            />
          </label>
        )}

        {selectedNode !== null && tool === 'select' && selectionHint(selectedNode) !== null && (
          <p className={s.hint}>{selectionHint(selectedNode)}</p>
        )}

        {/* Le style ne vaut que pour les formes libres : un ouvrage a la couleur de son réseau,
            et une pastille « Alerte » qui ne change rien à un regard se lirait comme une panne. */}
        {styled && (
          <>
            <SectionLabel>Style</SectionLabel>
            <p className={s.styleLabel}>Trait</p>
            <div className={s.swatches}>
              {SCHEMATIC_STROKE_SWATCHES.map((value_) => (
                <button
                  key={value_}
                  type="button"
                  className={s.swatch}
                  data-stroke={value_}
                  aria-label={SCHEMATIC_STROKE_LABELS[value_]}
                  aria-pressed={shownStroke === value_}
                  disabled={disabled}
                  onClick={() => {
                    setStroke(value_)
                    restyle({ stroke: value_ })
                  }}
                />
              ))}
            </div>

            <p className={s.styleLabel}>Épaisseur</p>
            <div className={s.swatches}>
              {SCHEMATIC_STROKE_WIDTHS.map((value_) => (
                <button
                  key={value_}
                  type="button"
                  className={s.weight}
                  data-width={value_}
                  aria-label={SCHEMATIC_STROKE_WIDTH_LABELS[value_]}
                  aria-pressed={shownStrokeWidth === value_}
                  disabled={disabled}
                  onClick={() => {
                    setStrokeWidth(value_)
                    restyle({ strokeWidth: value_ })
                  }}
                />
              ))}
            </div>
          </>
        )}

        <p className={s.styleLabel}>Export</p>
        <Button
          tone="ghost"
          size="sm"
          disabled={!exportable || rendering}
          onClick={() => exportImage('button')}
        >
          <ImageDown size={18} aria-hidden />{' '}
          {rendering ? 'Image en cours…' : 'Télécharger l’image'}
        </Button>
        {renderFailed && (
          <p className={s.hint} role="status">
            La tablette n’a pas pu produire l’image. Le schéma reste dans le relevé — seule la copie
            d’archive manque.
          </p>
        )}
        {exportedAt !== null && !renderFailed && !exportOutdated && (
          <p className={s.hint}>
            {/* L'heure de l'export, à l'heure de Paris comme l'horodatage des pièces jointes. */}
            Image du schéma jointe · {appointmentSlot(exportedAt)?.time}
          </p>
        )}
        {/* ⚠️ Cette phrase **remplace** l'heure de l'export, elle ne s'y ajoute pas : deux
            lignes dont l'une rassure laisseraient l'agent lire la première. Le bureau ne voit
            que l'image, et le contrôle devient immuable à la transmission. */}
        {exportOutdated && !renderFailed && (
          <p className={s.staleHint} role="status">
            L’image jointe est antérieure à votre dernière retouche · téléchargez-la à nouveau.
          </p>
        )}
        {/* Un bouton grisé et muet se lit comme une panne (M21) : la raison est à côté. ⚠️ Elle
            suit le **plan**, pas le verrou : sur un contrôle transmis, cette phrase s'affichait
            sous un dessin bien visible, et une raison inexacte est pire qu'aucune. Le verrou,
            lui, est déjà dit par la pastille de l'en-tête et le bandeau de la fiche. */}
        {empty && <p className={s.hint}>Rien à télécharger tant que le plan est vide.</p>}

        {/* Un plan qui cesse silencieusement de répondre au doigt se lit comme une panne de
            l'app (M21), et l'agent n'a aucune raison de soupçonner un plafond. */}
        {planFull && (
          <p className={s.staleHint} role="status">
            Le plan porte {MAX_SCHEMATIC_NODES} formes, le maximum : l’éditeur n’en pose plus.
            Supprimez-en une pour continuer.
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * ⚠️ Fabriqué **une fois**, hors du composant : passé en valeur par défaut d'une prop, il serait
 * recréé à chaque rendu et changerait l'identité d'une dépendance à chaque frappe.
 */
const DEFAULT_IMAGE_PORT = createSchematicImagePort()

/** Le silence après le dernier geste au bout duquel l'image d'archive se refait. */
const AUTO_EXPORT_DELAY_MS = 1_500

/** Le pas de la grille aimantée, en unités de document : celui du quadrillage de GHP DIGIT. */
const GRID_STEP = 50

/** À quelle distance du premier angle un appui referme la maison, à 100 % de zoom. */
const CLOSE_RADIUS = 30

/**
 * L'écart qu'un trait à main levée peut perdre au relâchement, à 100 % de zoom : moins de deux
 * pixels d'écran, invisible, et c'est ce qui ramène des centaines de points à quelques dizaines.
 */
const FREEHAND_TOLERANCE = 4

/**
 * Ce qu'un geste en cours transporte — jamais dans l'état React : il change à chaque pixel.
 *
 * `pointer` est le contact qui l'a ouvert : lui seul le prolonge et le referme.
 */
type Gesture = { pointer: number } & (
  | { kind: 'pan'; client: { x: number; y: number } }
  | { kind: 'erase' }
  | { kind: 'move'; id: string; point: SchematicPoint }
  | { kind: 'draw'; id: string; shape: string }
  | { kind: 'vertex'; id: string; index: number; base: NetworkSchematic }
  | {
      kind: 'pinch'
      second: number
      distance: number
      view: SchematicViewport
      client: { clientX: number; clientY: number }
    }
)

type Handle = { kind: 'vertex' | 'insert'; index: number }

/**
 * Les formes **posées d'une touche** : elles n'ont qu'un point, et un glissement ne les étire
 * pas. Les autres se tirent, donc elles naissent avec deux points confondus.
 */
const ANCHORED: ReadonlySet<string> = new Set([
  'manhole',
  'inspectionChamber',
  'trap',
  'septicTank',
  'text',
  'manholeCover',
  'gratedInlet',
  'floorDrain',
  'liftingStation',
  'downpipe',
  'plug',
  'trapHorizontal',
  'trapVertical',
  'roomLabel',
  'outletLabel',
])

/** Les formes posées **d'appui en appui** : la maison et le préau. */
const TAP_DRAWN: ReadonlySet<string> = new Set(['polygon', 'shelter'])

/** Les traits d'un réseau : tirés d'un geste (droits) ou posés d'appui en appui (coudés). */
const LINEAR: ReadonlySet<string> = new Set(['wastewaterPipe', 'stormwaterPipe', 'publicNetwork'])

/** Ce qu'« Aligner » redresse : les formes faites de plusieurs segments. */
const ALIGNABLE: ReadonlySet<string> = new Set([
  'polygon',
  'shelter',
  'wastewaterPipe',
  'stormwaterPipe',
  'publicNetwork',
  'line',
  'arrow',
])

/** Les formes libres, dont l'agent choisit la couleur et l'épaisseur. */
const FREE_SHAPES: ReadonlySet<string> = new Set([
  'freehand',
  'line',
  'arrow',
  'rectangle',
  'ellipse',
  'text',
])

function isAnchored(kind: string): boolean {
  return ANCHORED.has(kind)
}

/** L'épaisseur d'un trait du catalogue : fine pour le caniveau, moyenne pour une conduite. */
function strokeFor(kind: string): SchematicStrokeWidth {
  return kind === 'gratedChannel' ? 'thin' : 'medium'
}

function catalogueItem(id: string): CatalogueItem | null {
  for (const family of SCHEMATIC_CATALOGUE) {
    const item = family.items.find((candidate) => candidate.id === id)
    if (item !== undefined) return item
  }
  return null
}

/** Les réseaux qu'un élément typé sélectionné peut prendre, dans l'ordre du sélecteur. */
function networkOptionsOf(kind: string): readonly {
  value: PaletteNetwork
  label: string
}[] {
  if (kind === 'publicNetwork') return SCHEMATIC_NETWORK_OPTIONS
  if (isSeparateNetworkKind(kind)) return SCHEMATIC_SEPARATE_NETWORK_OPTIONS
  return SCHEMATIC_STRUCTURE_NETWORK_OPTIONS
}

/** Le réseau d'un élément typé, tel que le sélecteur le montre. */
function networkOf(node: SchematicNode): PaletteNetwork {
  return node.stroke === 'stormwater' || node.stroke === 'combined' || node.stroke === 'wastewater'
    ? node.stroke
    : 'ink'
}

/** La consigne de l'outil, écrite en tête de la colonne de droite : rien ne s'affiche au survol. */
function hintFor(tool: string, draftNode: SchematicNode | null): string | null {
  if (draftNode?.kind === 'polygon') {
    return 'Un appui par angle. Le premier angle entouré, ou « Terminer », ferme la maison.'
  }
  if (draftNode?.kind === 'shelter') return 'Un appui par angle, puis « Terminer ».'
  if (draftNode !== null) {
    return 'Posez les coudes, puis touchez l’ouvrage d’arrivée : la conduite s’y accroche.'
  }
  if (tool === 'polygon') return 'Touchez le premier angle de la maison.'
  if (tool === 'shelter') return 'Touchez le premier angle du préau.'
  if (tool === 'pipe' || tool === 'publicNetwork') {
    return 'Tirez un trait droit, ou touchez d’appui en appui pour le couder. Partir d’un ouvrage l’y accroche.'
  }
  if (tool === 'eraser') {
    return 'Touchez une forme pour l’effacer, ou glissez : tout ce que le doigt traverse s’efface. « Annuler » ramène le geste entier.'
  }
  if (tool.startsWith('roomLabel:') || tool.startsWith('outletLabel:')) {
    return 'Chaque appui pose l’étiquette ; l’outil reste actif.'
  }
  if (tool === 'downpipeOutfall') {
    return 'Touchez la gouttière, puis glissez dans le sens où l’eau part.'
  }
  return null
}

function selectionHint(node: SchematicNode): string | null {
  if (node.points.length > 1 && ALIGNABLE.has(node.kind)) {
    return '« Aligner » redresse les murs à moins de 10°. Un « + » ajoute un angle.'
  }
  if (node.kind === 'gratedChannel') return 'Tirez une extrémité pour faire tourner le caniveau.'
  if (node.kind === 'downpipeOutfall') {
    return 'Tirez la poignée, entre les deux pointes, pour changer le sens du rejet.'
  }
  return null
}

/**
 * Une forme **tirée** dont les deux extrémités sont au même endroit : le doigt s'est posé et
 * relevé sans bouger.
 *
 * Les formes posées d'une touche ({@link ANCHORED}) n'ont qu'un point et ne passent jamais par
 * là — ce relâchement sur place **est** leur geste.
 */
function isVoidDrag(schematic: NetworkSchematic, id: string): boolean {
  const points = schematic.nodes.find((node) => node.id === id)?.points
  const [first, second] = points ?? []

  if (points?.length !== 2 || first === undefined || second === undefined) return false

  return first.x === second.x && first.y === second.y
}

function startingPoints(kind: string, point: SchematicPoint): SchematicPoint[] {
  return isAnchored(kind) || TAP_DRAWN.has(kind) ? [point] : [point, point]
}

function mapPoints(
  schematic: NetworkSchematic,
  id: string,
  change: (points: SchematicPoint[]) => SchematicPoint[],
): NetworkSchematic {
  return {
    ...schematic,
    nodes: schematic.nodes.map((node) =>
      node.id === id ? { ...node, points: change(node.points) } : node,
    ),
  }
}

/** Le dessin, avec le premier point du tracé en cours s'il n'est pas encore rangé. */
function withPending(schematic: NetworkSchematic, pending: SchematicNode | null): NetworkSchematic {
  if (pending === null || schematic.nodes.some((node) => node.id === pending.id)) return schematic
  return addNode(schematic, pending)
}

/**
 * L'extrémité d'une conduite tirée par sa poignée : posée sur un ouvrage, elle s'y accroche et se
 * cale sur son bord ; posée dans le vide, elle se décroche. ⚠️ Gardée en mémoire sans l'être à
 * l'écran, l'accroche faisait sauter l'extrémité vers l'ouvrage au moindre déplacement de celui-ci.
 */
function reattached(sketch: NetworkSchematic, id: string, index: number): NetworkSchematic {
  const node = sketch.nodes.find((candidate) => candidate.id === id)
  if (node === undefined || !LINEAR.has(node.kind)) return sketch
  const last = node.points.length - 1
  if (index !== 0 && index !== last) return sketch

  const end = node.points[index]
  const neighbor = node.points[index === 0 ? 1 : last - 1]
  if (end === undefined || neighbor === undefined) return sketch

  const target = structureAt(sketch, end)
  const placed =
    target === null ? sketch : moveVertex(sketch, id, index, edgeToward(target, neighbor))
  const held = target?.id ?? null
  return setNodeEnds(placed, id, index === 0 ? { from: held } : { to: held })
}

/** Une unité d'écran en unités de document : les repères gardent leur taille au zoom. */
function unitOf(view: SchematicViewport): number {
  return view.width / SCHEMATIC_VIEW.width
}

function distanceOf(
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number },
): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

/**
 * La grille aimantée, sur tout le plan à l'écran.
 *
 * ⚠️ Le plan garde ses proportions : quand la zone n'a pas celles de la vue, deux bandes restent
 * de part et d'autre, hors de la vue mais à l'écran. La grille déborde donc de la vue d'une
 * largeur de vue de chaque côté, ce qui couvre une zone jusqu'à trois fois plus large ou plus
 * haute que le plan.
 */
function GridLines({ view }: { view: SchematicViewport }) {
  const lines = []
  const margin = Math.max(view.width, view.height)
  const x0 = view.x - margin
  const y0 = view.y - margin
  const x1 = view.x + view.width + margin
  const y1 = view.y + view.height + margin
  for (let x = Math.floor(x0 / GRID_STEP) * GRID_STEP; x <= x1; x += GRID_STEP) {
    lines.push(<line key={`x${x}`} x1={x} y1={y0} x2={x} y2={y1} className={s.gridLine} />)
  }
  for (let y = Math.floor(y0 / GRID_STEP) * GRID_STEP; y <= y1; y += GRID_STEP) {
    lines.push(<line key={`y${y}`} x1={x0} y1={y} x2={x1} y2={y} className={s.gridLine} />)
  }
  return (
    <g data-export="skip" aria-hidden>
      {lines}
    </g>
  )
}

/**
 * Les poignées de la forme sélectionnée : ses angles à tirer, et un « + » au milieu de chaque mur
 * pour en ajouter un. Un symbole posé d'un appui n'a que son cadre : sa taille est fixe (#581).
 */
function Handles({
  node,
  unit,
  vertex,
}: {
  node: SchematicNode
  unit: number
  vertex: number | null
}) {
  // Une douzaine de pixels d'écran pour la poignée, et une cible invisible d'une trentaine
  // autour : le doigt la trouve sans que le plan disparaisse sous les repères.
  const size = 28 * unit
  const target = 36 * unit

  if (node.points.length === 1) {
    const center = node.points[0]
    const extent = symbolExtent(node)
    if (center === undefined || extent === 0) return null
    const reach = extent + 8 * unit
    return (
      <rect
        className={s.frame}
        x={center.x - reach}
        y={center.y - reach}
        width={reach * 2}
        height={reach * 2}
      />
    )
  }

  // ⚠️ Le « + » ne va qu'aux formes à murs : sur un rectangle ou un caniveau, deux points, il
  // tombait au centre de la forme, et la saisir par le milieu pour la déplacer la déformait.
  const ring = !ALIGNABLE.has(node.kind)
    ? []
    : node.kind === 'polygon'
      ? [...node.points, node.points[0]]
      : node.points

  return (
    <>
      {ring.slice(1).map((point, index) => {
        const previous = ring[index]
        if (point === undefined || previous === undefined) return null
        const x = (previous.x + point.x) / 2
        const y = (previous.y + point.y) / 2
        return (
          <g key={`insert-${index}`} data-handle="insert" data-index={index + 1}>
            <circle className={s.handleTarget} cx={x} cy={y} r={target} />
            <circle className={s.insert} cx={x} cy={y} r={size / 2} />
            <path
              className={s.insertMark}
              d={`M ${x - size / 4} ${y} h ${size / 2} M ${x} ${y - size / 4} v ${size / 2}`}
            />
          </g>
        )
      })}
      {node.points.map((point, index) =>
        // Un trait à main levée ne montre que ses deux bouts : une poignée par point le couvrait.
        // La gouttière sur sol, que sa pointe : la gouttière se déplace avec la forme (#581).
        (node.kind === 'freehand' && index !== 0 && index !== node.points.length - 1) ||
        (node.kind === 'downpipeOutfall' && index === 0) ? null : (
          <g key={`vertex-${index}`} data-handle="vertex" data-index={index}>
            <circle className={s.handleTarget} cx={point.x} cy={point.y} r={target} />
            <rect
              className={s.handle}
              data-active={vertex === index ? '' : undefined}
              x={point.x - size / 2}
              y={point.y - size / 2}
              width={size}
              height={size}
              rx={3 * unit}
            />
          </g>
        ),
      )}
    </>
  )
}

/** Les repères d'un tracé d'appui en appui : angles posés, premier angle entouré, trait qui suit. */
function DraftMarks({
  node,
  hover,
  unit,
  origin,
}: {
  node: SchematicNode
  hover: SchematicPoint | null
  unit: number
  origin: SchematicNode | null
}) {
  const first = node.points[0]
  const last = node.points.at(-1)
  const originCenter = origin?.points[0]

  return (
    <g pointerEvents="none">
      {last !== undefined && hover !== null && (
        <line className={s.rubber} x1={last.x} y1={last.y} x2={hover.x} y2={hover.y} />
      )}
      {node.kind === 'polygon' && first !== undefined && (
        <circle className={s.firstCorner} cx={first.x} cy={first.y} r={18 * unit} />
      )}
      {origin !== null && originCenter !== undefined && (
        <circle
          className={s.firstCorner}
          cx={originCenter.x}
          cy={originCenter.y}
          r={symbolExtent(origin) + 12 * unit}
        />
      )}
      {node.points.map((point, index) => (
        <circle key={index} className={s.draftPoint} cx={point.x} cy={point.y} r={7 * unit} />
      ))}
    </g>
  )
}

/**
 * La forme touchée, ou `null` pour le fond du plan.
 *
 * ⚠️ C'est le test de collision, et il ne coûte rien : le navigateur a déjà routé l'événement
 * vers l'élément SVG touché, `closest` remonte au groupe qui porte l'identifiant. C'est la
 * moitié de l'argument « SVG plutôt que canvas » — l'autre étant qu'aucun repaint n'est à
 * écrire.
 */
function nodeIdAt(target: EventTarget): string | null {
  if (!(target instanceof Element)) return null

  return target.closest('[data-node-id]')?.getAttribute('data-node-id') ?? null
}

/** La poignée touchée sur la forme sélectionnée, ou `null`. */
function handleAt(target: EventTarget): Handle | null {
  if (!(target instanceof Element)) return null
  const handle = target.closest('[data-handle]')
  const kind = handle?.getAttribute('data-handle')
  const index = Number(handle?.getAttribute('data-index'))
  if (kind !== 'vertex' && kind !== 'insert') return null
  return { kind, index }
}

function borderOf(board: SVGSVGElement, side: 'left' | 'top' | 'right' | 'bottom'): number {
  const width = Number.parseFloat(
    window.getComputedStyle(board).getPropertyValue(`border-${side}-width`),
  )

  return Number.isFinite(width) ? width : 0
}
